/**
 * KI-Spur — wie ein KI-Vorschlag entstand (Etappe 3, 28.09.2026).
 * Server: hetzner/webhook/kontor_ki_spur.py (nur lesend), Daten aus ki_spur_store.
 */
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { apiGet } from "@vimaana/ui";

export type KiQuelle = { typ: string; id: string; titel?: string; zeitpunkt?: string; abgeschnitten?: boolean; nachgelesen?: boolean };

export type KiEreignis = {
  art: "gezeigt" | "uebernommen" | "geaendert" | "verworfen" | "gesendet" | "angewandt" | string;
  teil: string;
  wer: string;
  am: string;
  text_vorher?: string | null;
  text_nachher?: string | null;
  verweis?: string;
};

export type KiAuftrag = {
  id: string;
  art: string;
  bezug_id: string;
  bezug: string;
  status: string;
  vorlage: string;
  modell: string;
  quellen: KiQuelle[];
  denken: string | null;
  tokens_ein: number | null;
  tokens_aus: number | null;
  rechenzeit_ms: number | null;
  bearbeiter: string;
  fehler: string;
  versuche: number;
  paperclip_lauf: string | null;
  paperclip_ticket: string | null;
  created_at: string;
  fertig_am: string;
  angewandt_am: string;
  prompt: string | null;
  eilig: string | null;
  ereignisse: KiEreignis[];
};

export type KiListenEintrag = {
  id: string;
  art: string;
  bezug_id: string;
  bezug: string;
  status: string;
  vorlage: string;
  modell: string | null;
  bearbeiter: string;
  created_at: string;
  fertig_am: string;
  tokens_ein: number | null;
  tokens_aus: number | null;
  rechenzeit_ms: number | null;
  paperclip_ticket: string | null;
  quellen_anzahl: number | null;
  kurz: string;
  ereignisse: { art: string; teil: string; wer: string; am: string }[];
};

export type KiQuote = {
  art: string; vorschlaege: number; uebernommen: number; geaendert: number; verworfen: number;
  gesendet: number; angewandt: number; ohne_entscheidung: number;
};

export type KiStand = { evo_wach: boolean; zuletzt: string; pausiert: string[] };

export type KiFilter = { art?: string; bezug_id?: string; entscheidung?: string; limit?: number };

function abfrage(f: KiFilter, vor?: string): string {
  const q = new URLSearchParams();
  if (f.art) q.set("art", f.art);
  if (f.bezug_id) q.set("bezug_id", f.bezug_id);
  if (f.entscheidung) q.set("entscheidung", f.entscheidung);
  q.set("limit", String(f.limit || 40));
  if (vor) q.set("vor", vor);
  return q.toString();
}

export function useKiAuftrag(id: string | undefined, aktiv = true) {
  return useQuery<KiAuftrag>({
    queryKey: ["ki-spur", "auftrag", id],
    queryFn: () => apiGet<KiAuftrag>(`/api/kontor/ki-spur/${id}`),
    enabled: !!id && aktiv,
    staleTime: 30_000,
  });
}

/** Die neuesten Aufträge zu einem Bezug (Beleg, Projekt) — für die Klappe am Beleg. */
export function useKiZuBezug(bezugId: string | undefined, aktiv: boolean) {
  return useQuery<{ auftraege: KiListenEintrag[] }>({
    queryKey: ["ki-spur", "bezug", bezugId],
    queryFn: () => apiGet<{ auftraege: KiListenEintrag[] }>(`/api/kontor/ki-spur?${abfrage({ bezug_id: bezugId, limit: 5 })}`),
    enabled: !!bezugId && aktiv,
    staleTime: 30_000,
  });
}

/** Die Liste auf der Seite KI-Spur, seitenweise („Ältere laden"). */
export function useKiListe(f: KiFilter) {
  return useInfiniteQuery({
    queryKey: ["ki-spur", "liste", f],
    queryFn: ({ pageParam }) => apiGet<{ auftraege: KiListenEintrag[] }>(`/api/kontor/ki-spur?${abfrage(f, pageParam || undefined)}`),
    initialPageParam: "",
    getNextPageParam: (letzte) => {
      const a = letzte.auftraege;
      return a.length >= (f.limit || 40) ? a[a.length - 1].created_at : undefined;
    },
    staleTime: 30_000,
  });
}

export function useKiQuoten(tage = 90) {
  return useQuery<{ tage: number; arten: KiQuote[] }>({
    queryKey: ["ki-spur", "quoten", tage],
    queryFn: () => apiGet<{ tage: number; arten: KiQuote[] }>(`/api/kontor/ki-spur/quoten?tage=${tage}`),
    staleTime: 60_000,
  });
}

export function useKiStand() {
  return useQuery<KiStand>({
    queryKey: ["ki-spur", "stand"],
    queryFn: () => apiGet<KiStand>("/api/kontor/ki-spur/stand"),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

// ── Wörter ──────────────────────────────────────────────────────────────────

export const KI_ART: Record<string, string> = {
  clara_antwort: "Mailentwurf",
  beleg_text: "Beleg (Text)",
  beleg_bild: "Beleg (Bild)",
};

export const KI_EREIGNIS: Record<string, string> = {
  gezeigt: "gezeigt",
  uebernommen: "übernommen",
  geaendert: "geändert übernommen",
  verworfen: "verworfen",
  gesendet: "gesendet",
  angewandt: "angewandt",
};

export function kiTeil(teil: string): string {
  if (!teil) return "";
  if (teil === "entwurf") return "Entwurf";
  return teil;
}

/** „gpt-oss-120b-MXFP4.gguf" → „gpt-oss-120b · lokal". */
export function kiModell(m: string | null | undefined): string {
  const name = (m || "").replace(/\.gguf$/i, "").replace(/-(MXFP4|Q\d.*|F16|BF16)$/i, "");
  if (!name) return "";
  return /claude/i.test(name) ? `${name} · Claude (Rückfall)` : `${name} · lokal auf dem Evo`;
}

export function kiDauer(ms: number | null | undefined): string {
  if (ms == null) return "";
  return ms < 1000 ? `${ms} ms` : ms < 120_000 ? `${Math.round(ms / 1000)} s` : `${(ms / 60_000).toFixed(1).replace(".", ",")} min`;
}
